import Logo from "~/components/Logo"
import {
  accompanyingPrevious,
  articleBodyStyle,
  articleCompareImages,
  articleFaqBodyStyle,
  articleImageStyle,
  attribution,
  bold,
  faqGrid,
  faqH3Style,
  figCaptionStyle,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  imagePreviewBodyStyle,
  infoWrap,
  orderedList,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Link from "~/components/Link"
import Footer from "~/components/Footer"
import { golden, vars } from "~/components/styles/theme.css"
import { Bold, H2, H3, P, Section } from "~/components/articleComponents"
import ScrollyWolly from "~/components/ScrollyWolly"

// SEO FIX: Title shortened from 69 to 52 chars (was over 60 char limit)
const TITLE = "Features of Urban Gardens | Smart City Garden Design"
const DESCRIPTION =
  "A complete guide to creating a beautiful and functional city garden. Explore the essential features  from zoning and vertical planting to privacy."
const URL = "https://rescapeai.com/garden-design-guide/urban/features-of-urban-gardens"
const IMAGE = "https://rescapeai.com/articles/cottage-garden-winding-pathways.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    // SEO FIX: Added Twitter Card tags
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: TITLE,
    },
    {
      name: "twitter:description",
      content: DESCRIPTION,
    },
    {
      name: "twitter:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: URL,
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-22",
        dateModified: "2025-10-22",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": URL,
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/guide/urban-heading.jpg)",
          backgroundPositionY: "70%",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>Design Guide: Features of Urban Gardens</h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <div className={page}>
        <section className={stepSectionStyle}>
          <Bold>Header Image</Bold>
          <p>
            <a
              className={bold}
              style={{ fontSize: ".8rem" }}
              href="https://www.rosemarycoldstream.com/"
              target="_blank"
            >
              Designer: Rosemary Coldstream
            </a>
            <br />
            <a
              className={bold}
              style={{ fontSize: ".8rem" }}
              href="https://www.rhs.org.uk/shows-events/rhs-chelsea-flower-show/history/2023/gardens/feels-like-home"
              target="_blank"
            >
              Image attribution: RHS
            </a>
          </p>
          <h2 className={h2Style}>What Makes an Urban Garden</h2>
          <p className={articleBodyStyle}>
            Urban gardens — whether small backyards, rooftop terraces, balconies, or
            compact courtyards — are more than leftover patches of land. When designed
            well, they become functional, relaxing, and ecologically valuable outdoor
            spaces. Key to a good urban garden is how it makes the most of limited space,
            balances built and living elements, and adapts to the realities of city life:
            light, noise, neighbours, micro-climate, and mobility.
          </p>
          <figure>
            <ScrollyWolly
              height={`${golden * 10}rem`}
              yPosition="0%"
              before={{
                image: "/guide/garden-6.webp",
                title: "Before",
                alt: "Patiod garden area",
              }}
              after={{
                image: "/guide/living-wall-2.webp",
                title: "",
                alt: "Totally redesigned with Rescape AI",
              }}
            />
            <figcaption className={figCaptionStyle}>
              The AI has replaced the fence with a living wall with containerised shrubs
              bordering the garden. Inside recommended is a seating area.{" "}
              <Link href="/garden-design-guide/contemporary/contemporary-garden-ideas-using-ai">
                See Contemporary Ideas
              </Link>{" "}
              or <Link href="/hello/signup">Join now</Link>
            </figcaption>
          </figure>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Core Principles for Urban Gardens</h2>
          <p className={articleBodyStyle}>
            When planning an urban garden, these principles help guide design so the space
            feels purposeful, comfortable, and alive:
          </p>
          <ul className={unorderedList}>
            <li>
              Efficiency & intelligent layout: Make every square metre count — thoughtful
              zoning, vertical usage, multipurpose design.
            </li>
            <li>
              Balance between structure and softness: Use hard-landscape features (paving,
              decking, walls, containers) in harmony with planting and living materials.
            </li>
            <li>
              Adaptability to environment: Use materials and plants that reflect the
              reality of urban constraints — shade, limited soil, wind, proximity to
              buildings or paved surfaces.
            </li>
            <li>
              Functionality and comfort: Ensure the garden works for your needs —
              relaxation, socialising, growing plants — while being easy to maintain.
            </li>
            <li>
              Ecological and environmental value: Incorporate elements that support
              biodiversity, improve air quality, manage water, and make the garden part of
              a sustainable urban ecosystem.
            </li>
          </ul>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Typical Features of Well-Designed Urban Gardens</h2>
          <p className={articleBodyStyle}>
            Below are common features that make successful urban gardens — many of them
            draw also on contemporary-garden design ethos, but are tailored to the urban
            context.
          </p>
          <h3 className={h3Style}>1. Defined Zones & Multifunctional Layout</h3>
          <figure className={accompanyingPrevious}>
            <img
              style={{ maxHeight: "360px", objectFit: "cover", marginTop: "0.5rem" }}
              className={articleImageStyle}
              src="/guide/seating-area.webp"
              alt="AI generated seating area recommendation for urban garden zoning"
            />
            <figcaption className={figCaptionStyle}>
              An AI generated seating area reccomendation done on Rescape AI. Created with
              a mask and creativity set to 'Dream mode (100%)'
            </figcaption>
          </figure>
          <p className={articleBodyStyle}>
            Because space is limited, urban gardens often benefit from clear zoning:
          </p>
          <P>
            Seating / relaxation area — A small patio, deck or paved area for a table and
            chairs or bench. Helps turn the garden into an outdoor living "room."
          </P>
          <P>
            Planting / growing area — Raised beds, containers or small beds for flowers,
            shrubs, herbs, vegetables — defined separately from seating to avoid clutter.{" "}
          </P>
          <P>
            Vertical or overhead space used — Trellises, walls, fences, containers that
            use height instead of width to add planting without eating up floor space.
          </P>
          <P>
            Flexibility and multipurpose features — Built-in seating with storage,
            planters that double as screens, small patios that convert to dining or lounge
            areas — to maximise usability.
          </P>
          <P>
            This zoning helps a small garden feel organised, purposeful, and larger than
            it is.
          </P>
          <H3>2. Hardscape + Softscape Balance: Structure Meets Nature</H3>
          <figure className={accompanyingPrevious}>
            <img
              style={{ maxHeight: "360px", objectFit: "cover", marginTop: "0.5rem" }}
              className={articleImageStyle}
              src="/guide/urban-garden.jpg"
              alt="Real life urban garden showing hardscape and softscape balance"
            />
            <figcaption className={figCaptionStyle}>
              Real life urban garden. Urban gardens often take ideas from{" "}
              <Link href="/garden-design-guide/contemporary/features-of-contemporary-gardens">
                Contemporary gardens
              </Link>
            </figcaption>
          </figure>
          <P>
            A successful urban garden often blends structural elements with living
            planting — the hardscape gives form and function, the softscape brings life,
            texture, colour:
          </P>
          <P>
            Hardscape features: paving or decking for seating/paths; fences or walls for
            privacy or boundary; containers and raised beds if ground soil is poor or
            absent.
          </P>
          <P>
            Soft elements: plants — shrubs, climbing plants, perennials, small trees,
            herbs — carefully chosen to suit light, space, and maintenance constraints.
          </P>
          <P>
            Geometry and clean lines (if inspired by modern/contemporary style): Simple
            paving shapes or decking, linear layout — these give order, and work
            especially well when paired with structured planting or minimalistic design.
          </P>
          <P>
            Such balance ensures the garden remains usable and inviting, while still
            feeling natural and alive.
          </P>
          <H3>3. Vertical & Creative Use of Space</H3>
          <figure className={accompanyingPrevious}>
            <img
              style={{ maxHeight: "360px", objectFit: "cover", marginTop: "0.5rem" }}
              className={articleImageStyle}
              src="/guide/living-wall-3.webp"
              alt="Living wall vertical planting solution for urban garden space"
            />
            <figcaption className={figCaptionStyle}>
              Generated on Rescape AI. Creativity set to 'Dream mode' and using a mask
            </figcaption>
          </figure>
          <P>
            Space constraints are often the biggest challenge in cities. Urban gardens
            often overcome this by thinking vertically and creatively:
          </P>
          <P>
            Climbers and vertical planting — Using walls, fences, trellises or vertical
            planters to grow plants upward. This increases green coverage without
            requiring ground space.
          </P>
          <P>
            Green walls or living walls / containers on balconies/roofs — Where soil
            ground is unavailable, green walls or container-based planting help introduce
            greenery, enhance air quality, and make use of available vertical surfaces.
          </P>
          <P>
            Compact planting and layering — Combining tall, medium, and low-height plants
            to create a layered effect: useful for privacy, shade, and visual interest
            even in tight spaces.
          </P>
          <P>
            By building upward rather than outward, urban gardens squeeze much more value
            from limited footprints.
          </P>
          <H3>4. Environmental & Ecological Features</H3>
          <figure className={accompanyingPrevious}>
            <img
              style={{ maxHeight: "360px", objectFit: "cover", marginTop: "0.5rem" }}
              className={articleImageStyle}
              src="/guide/planters.webp"
              alt="Container planters supporting biodiversity in an urban garden"
            />
            <figcaption className={figCaptionStyle}>
              <Link href="https://rescapeai.com">Rescape AI</Link> recommended planting area.
              Generated with a mask and at 80%
            </figcaption>
          </figure>
          <P>
            Urban gardens can — and arguably should — contribute positively to urban
            ecology, climate resilience, and sustainability. Some of their notable
            environmental features are:
          </P>
          <P>
            Biodiversity support — A mosaic of gardens (private and communal) across a
            city can create corridors and habitats for wildlife (birds, insects, small
            animals), improving urban biodiversity.
          </P>
          <P>
            Air quality and micro-climate improvement — Plants help filter air, absorb
            CO₂, release oxygen and moisture, and can help moderate temperature — offering
            natural cooling in built-up areas.
          </P>
          <P>
            Water management & sustainable drainage — Features like rain gardens or
            permeable surfaces (in place of impermeable paving) can help manage rainwater,
            reduce runoff, and prevent flooding — especially helpful in dense urban areas.
          </P>
          <P>
            Adaptation to urban climate challenges — Urban gardens can buffer heat, reduce
            urban-heat-island effects, offer shelter from wind or traffic noise, and
            create a more comfortable micro-environment for people and wildlife.
          </P>
          <H3>5. Comfort, Privacy & Living-Space Extension</H3>
          <P>
            Often, the value of an urban garden is not just in plants, but in the extra
            living space — a private outdoor refuge or social area. Features that support
            this include:
          </P>
          <P>
            Seating and dining zones — Even a small courtyard or paved area can host a
            table and chairs for meals, morning coffee, socialising or quiet time.
          </P>
          <P>
            Screening and boundaries for privacy — Fences, walls, tall planters, hedges or
            trellises with climbing plants help create a sense of enclosure and privacy in
            dense urban neighbourhoods.
          </P>
          <P>
            This turns an urban garden into a genuine extension of home — a place to
            relax, socialise or reconnect with nature.
          </P>
        </section>
        <Section>
          <H2>Challenges in Urban Gardens — and How Good Design Helps</H2>
          <P>
            Urban gardens often bloom under constraints: tight footprints, odd pockets of
            light, stubbornly poor soil—or none at all—along with gusty wind, city grime,
            privacy gaps, and the reality that maintenance must stay manageable. Yet with
            a bit of design alchemy, each challenge can become a springboard for
            creativity.
          </P>
          <P>
            When the ground is stingy or paved over entirely, the solution is to build
            upward or outward with intention. Containers, raised beds, planters, and
            vertical structures allow roots to thrive where soil cannot. They turn patios,
            balconies, and rooftops into layered growing environments.
          </P>
          <P>
            Shifting shade and inconsistent sunlight call for a cast of plants that are
            comfortable in softer light. Pale surfaces can bounce brightness into dimmer
            corners, while vertical frames and tiered arrangements help every leaf catch
            whatever light wanders in.
          </P>
          <P>
            For urban soundscapes and exposed edges, planting becomes both shield and
            sanctuary. Screens, trellises, fences, hedges, and even murmuring water
            features can soften noise and shape a sense of enclosure, transforming a
            vulnerable space into a small refuge within the city.
          </P>
          <P>
            Good urban garden design doesn't try to replicate a large country garden.
            Instead it works with the constraints — making the garden efficient,
            beautiful, functional and sustainable.
          </P>
        </Section>
        <Section>
          <H2>Why Urban Gardens Matter — Beyond Just a Bit of Green</H2>
          <P>Urban gardens offer benefits that go beyond aesthetics:</P>
          <P>
            Improved urban ecology and biodiversity: Each garden — private or shared —
            adds to a network of green spaces that support wildlife, pollinators, birds,
            and increase ecological connectivity across a city.
          </P>
          <P>
            Better air quality, micro-climate and comfort: Plants moderate urban heat,
            improve air, provide shade, and create a cooling, calming micro-environment.
          </P>
          <P>
            Well-being and lifestyle benefits: A garden — even a small one — offers a
            private outdoor space to relax, enjoy nature, grow food or plants, entertain,
            and escape the concrete hustle of city life.
          </P>
          <P>
            Sustainability and resilience: Through rain-water management, green
            infrastructure, native planting, container gardening, and biodiversity
            support, urban gardens contribute to more sustainable, resilient cities.
          </P>
          <P>
            In a time when cities grow denser and open green space becomes more precious —
            urban gardens represent a powerful, accessible way for individuals to
            reconnect with nature, support the environment, and build healthier, greener
            urban living.
          </P>
        </Section>
        <Section>
          <H2>Conclusion — Urban Gardens: More Than Just Small Green Spaces</H2>
          <P>
            Urban gardens remind us that you don't need wide meadows or sprawling land to
            enjoy the benefits of a garden. With thoughtful design, smart use of space,
            and purposeful features — from zoning and hard-soft balance to vertical
            planting, privacy, and ecological awareness — even the smallest city plot can
            become a living, breathing oasis.
          </P>
          <P>
            Whether the aim is a quiet retreat, a social outdoor room, a green sanctuary
            for wildlife, or a compact space to grow herbs and flowers — what matters is
            making design choices that respond to space, environment, and how you want to
            live in that garden.
          </P>
        </Section>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "2rem",
          }}
        >
          <LinkButton url="/hello/signup">Join Rescape AI</LinkButton>
        </div>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>FAQ's</h2>

          <div className={faqGrid}>
            <div>
              <h3 className={faqH3Style}>
                What makes urban garden design different from traditional garden design?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Urban garden design embraces the constraints of small, complex spaces
                rather than trying to mimic large country gardens. It focuses on
                efficiency, smart use of space, aesthetics, and sustainable solutions
                tailored to city environments.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>Why are urban gardens important in cities?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                They enrich urban ecology, support wildlife, and create green corridors
                that help reconnect fragmented ecosystems. Even small gardens contribute
                to biodiversity by offering food, shelter, and stepping-stone habitats for
                pollinators, insects, and birds.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                Can a small garden really improve well-being?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Absolutely. Even the tiniest balcony or courtyard can become a restorative
                space — a place to unwind, connect with nature, grow food or flowers, or
                simply enjoy a quiet moment outdoors away from the city buzz.
              </p>
            </div>
          </div>
        </section>
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
          <LinkButton url="/hello/signup">Join Rescape AI</LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
